/* eslint-disable @typescript-eslint/no-var-requires */
import { useEffect } from 'react';
import styled from 'styled-components';
import { Box, Heading } from '@99designs/emu';
import { spaces } from '@99designs/emu/settings';
import {
    vistaI18nRoutesBy99dLocale,
    vistaI18nRoutesBy99dLocaleAndCategory,
} from '@99designs/routing';
import { useSite } from '@99designs/spa-common';
import { appendUtmParamsToUrl } from '@99designs/tracking';
import events from '../../lib/events';
import PrintRecommendationItem from './PrintRecommendationItem';
import { RecommendationsCarouselContainer } from './RecommendationsCarouselContainer';

const flyersPostcard = require('./Images/flyersPostcard.jpg');
const morePrintOptions = require('./Images/morePrintOptions.jpg');
const productLabels = require('./Images/productLabels.jpg');
const tshirt = require('./Images/tshirt.jpg');

// A Swan library exists but as this is the only button that would be implemented
// we have opted to just manually code the CSS here instead. Should this change in the
// future, please update the styling for this button to use the library
const SwanButton = styled.a`
    display: flex;
    justify-content: center;
    min-width: 238px;
    padding: ${spaces['0.75bu']} ${spaces['1bu']};
    font-weight: 700;
    border-radius: ${spaces['0.5bu']};
    background: #6fd0f5;
    box-shadow: 0px -1px 1px 0px #00000017 inset;
    border: none;
    color: #313030;

    &:hover {
        color: #313030;
        text-decoration: none;
    }
`;

const genericItems = (locale: string) => {
    return [
        {
            title: __('Product labels'),
            altText: 'Product labels',
            previewImageUrl: productLabels,
            vpProductPageUrl: vistaI18nRoutesBy99dLocaleAndCategory(locale, 'label'),
        },
        {
            title: __('Flyers Postcard'),
            altText: 'Flyers Postcard',
            previewImageUrl: flyersPostcard,
            vpProductPageUrl: vistaI18nRoutesBy99dLocaleAndCategory(locale, 'postcard'),
        },
        {
            title: __('T-Shirt'),
            altText: 'T-Shirt',
            previewImageUrl: tshirt,
            vpProductPageUrl: vistaI18nRoutesBy99dLocaleAndCategory(locale, 'tshirt'),
        },
        {
            title: __('More print options'),
            altText: 'More print options',
            previewImageUrl: morePrintOptions,
            vpProductPageUrl: vistaI18nRoutesBy99dLocale(locale),
        },
    ];
};

function PrintRecommendationsBannerGeneric() {
    const site = useSite();
    const locale = site.siteConfig.locale;
    const genericItemsArray = genericItems(locale);

    useEffect(() => {
        events.trackRecommendationsViewed('your_work', 'generic');
    }, []);

    const handleExplorePrintClicked = () => {
        events.trackRecommendationsItemClicked(
            'your_work',
            appendUtmParamsToUrl(vistaI18nRoutesBy99dLocale(locale), {
                utm_source: '99designs',
                utm_medium: 'referral',
                utm_campaign: '99d_yourwork_recs_gen',
            }),
            'Explore print options',
            'generic'
        );
    };

    return (
        <>
            <Box paddingRight="3bu">
                <Heading accessibilityLevel={3}>
                    {__('You have a design to be proud of. Now show it off!')}
                </Heading>
            </Box>
            <Box display="flex" flexWrap="wrap">
                <Box
                    display={['none', 'none', 'block']}
                    minWidth="190px"
                    flex="1 1 190px"
                    marginY="1bu"
                    marginRight="1.5bu"
                >
                    <Box>
                        {__(
                            'Bring your design to life with VistaPrint and print your design onto (almost) anything.'
                        )}
                    </Box>
                    <Box marginTop="1bu">
                        <SwanButton
                            onClick={handleExplorePrintClicked}
                            href={appendUtmParamsToUrl(vistaI18nRoutesBy99dLocale(locale), {
                                utm_source: '99designs',
                                utm_medium: 'referral',
                                utm_campaign: '99d_yourwork_recs_gen',
                            })}
                            target="_blank"
                        >
                            {__('Explore print options')}
                        </SwanButton>
                    </Box>
                </Box>
                <RecommendationsCarouselContainer>
                    {genericItemsArray.map((item) => (
                        <PrintRecommendationItem
                            key={item.title}
                            title={item.title}
                            url={item.vpProductPageUrl}
                            image={item.previewImageUrl}
                            onClick={() => {
                                events.trackRecommendationsItemClicked(
                                    'your_work',
                                    item.vpProductPageUrl.split('?')[0],
                                    item.title,
                                    'generic'
                                );
                            }}
                            altText={item.altText}
                        />
                    ))}
                </RecommendationsCarouselContainer>
            </Box>
        </>
    );
}

export default PrintRecommendationsBannerGeneric;
